"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.contentFingerprint = contentFingerprint;
exports.fingerprintHash = fingerprintHash;
exports.contentHashOf = contentHashOf;
exports.worklogContentHashOf = worklogContentHashOf;
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C35 · what we last agreed on, in 64 characters.                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS IS WHAT TELLS AN ECHO FROM A CHANGE, AND WITHOUT IT THE TWO ARE
 * INDISTINGUISHABLE** (`jira.md` §4.2). We write a task out to Jira; Jira fires a webhook back at
 * us describing the change we just made; the webhook path applies it. Nothing in the payload says
 * *"you did this"* — Jira does not track who a change came from in any way we can read cheaply, and
 * §1.3 means we cannot verify by re-reading either.
 *
 * So we hash **the fields we sync**, store it on the link, and compare. Same hash, same state:
 * nothing to do. Different hash: somebody changed something that matters.
 *
 * ⚠️⚠️ **BOTH DIRECTIONS MUST COMPUTE IT IDENTICALLY, WHICH IS THE ONLY REASON THIS LIVES IN
 * `shared`.** If the inbound path and the outbound path hash even slightly differently — a
 * different null convention, an unsorted array — every write produces a hash that never matches and
 * the echo suppression silently does nothing. That failure has no symptom except a sync that writes
 * twice as much as it should.
 *
 * ⚠️ **It hashes what we SYNC, not what Jira sent.** A field nobody mapped changing in Jira must not
 * make this move: it would mark every issue as changed on the first run after somebody edits an
 * unrelated custom field, and the run log would be noise.
 */
/**
 * ⚠️ **A stable string before a hash, and every rule here exists to keep it stable.**
 *
 * - Keys are emitted in a fixed order, never `Object.keys`.
 * - Arrays are sorted, because Jira does not promise label order and a reordering is not a change.
 * - `null` and `''` collapse to the same token: a description cleared to empty and a description
 *   removed are the same state to us, and treating them differently would flap on every write.
 * - Mapped field values are sorted by field id and serialised with `JSON.stringify`, which is
 *   stable for the scalars and arrays these actually hold.
 */
function contentFingerprint(issue) {
    const nil = (value) => (value === null || value === '' ? ' ' : value);
    const num = (value) => (value === null ? ' ' : String(value));
    const fields = Object.keys(issue.fields)
        .sort()
        .map((key) => `${key}=${JSON.stringify(issue.fields[key] ?? null)}`)
        .join('');
    return [
        nil(issue.title),
        nil(issue.descriptionHtml),
        nil(issue.statusName),
        nil(issue.priorityName),
        nil(issue.assigneeRemoteUserId),
        num(issue.originalEstimateSeconds),
        num(issue.remainingEstimateSeconds),
        nil(issue.startsOn),
        nil(issue.dueOn),
        [...issue.labels].sort().join(','),
        nil(issue.parentRemoteIssueId),
        /* ⚠️ `0160` — ids only, sorted. A renamed component is not a change any rule can see. */
        issue.components
            .map((one) => one.id)
            .sort()
            .join(','),
        issue.fixVersions
            .map((one) => one.id)
            .sort()
            .join(','),
        nil(issue.epicRemoteIssueId),
        fields,
    ].join('');
}
/**
 * FNV-1a, 64 bits, as sixteen hex characters.
 *
 * ⚠️ **Not a cryptographic hash, and it does not need to be.** Nothing here is a secret and nobody
 * is trying to forge a collision — the question is only *"is this the same state we last saw?"*,
 * and a wrong answer costs one redundant write. What it does need is to be **identical in the API
 * and the browser and stable across releases**, which is why it is written out rather than taken
 * from `node:crypto` (absent in a browser) or a dependency (absent from this package by choice).
 */
function fingerprintHash(text) {
    /* 64-bit FNV-1a, in two 32-bit halves because JavaScript's bitwise operators are 32-bit. */
    let hi = 0xcbf2_9ce4;
    let lo = 0x8422_2325;
    for (let index = 0; index < text.length; index += 1) {
        const code = text.charCodeAt(index);
        lo ^= code & 0xffff;
        hi ^= (code >>> 16) & 0xffff;
        /* multiply by the 64-bit FNV prime 0x100000001b3, kept in 32-bit lanes */
        const loLow = lo & 0xffff;
        const loHigh = lo >>> 16;
        const hiLow = hi & 0xffff;
        const hiHigh = hi >>> 16;
        const p0 = loLow * 0x01b3;
        const p1 = loHigh * 0x01b3 + (p0 >>> 16);
        const p2 = hiLow * 0x01b3 + (p1 >>> 16) + loLow * 0x0100;
        const p3 = hiHigh * 0x01b3 + (p2 >>> 16) + loHigh * 0x0100;
        lo = ((p1 & 0xffff) << 16) | (p0 & 0xffff);
        hi = ((p3 & 0xffff) << 16) | (p2 & 0xffff);
        lo >>>= 0;
        hi >>>= 0;
    }
    return (hi >>> 0).toString(16).padStart(8, '0') + (lo >>> 0).toString(16).padStart(8, '0');
}
/** The one call both directions make. */
function contentHashOf(issue) {
    return fingerprintHash(contentFingerprint(issue));
}
/**
 * The same idea for an hour.
 *
 * ⚠️ **Its own function rather than a generic one**, because the two shapes share no fields and a
 * generic serialiser over `Object.entries` would be exactly the unstable-ordering bug the header
 * warns about.
 *
 * ⚠️ `remoteUpdatedAt` is excluded for the same reason it is on an issue: it moves on every write,
 * including ones that changed nothing we store.
 */
function worklogContentHashOf(worklog) {
    return fingerprintHash([
        worklog.remoteIssueId,
        worklog.remoteAuthorId ?? ' ',
        worklog.workedOn,
        worklog.startedAt ?? ' ',
        worklog.hours.toFixed(2),
        worklog.note ?? ' ',
    ].join(''));
}
//# sourceMappingURL=content-hash.js.map